import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment-timezone';

import 'moment/locale/nl';
import 'moment/locale/de';
import 'moment/locale/fr';
import 'moment/locale/ja';
import 'moment/locale/ko';
import 'moment/locale/pt';
import 'moment/locale/zh-cn';
import 'moment/locale/es';
import 'moment/locale/ru';
import 'moment/locale/ar';
import 'moment/locale/it';
import 'moment/locale/tr';
import 'moment/locale/hi';
import 'moment/locale/sv';
import 'moment/locale/nb';
import 'moment/locale/fi';
import 'moment/locale/pl';
import 'moment/locale/da';
import 'moment/locale/vi';
import antLocaleDE from 'antd/locale/de_DE';
import antLocaleNL from 'antd/locale/nl_NL';
import antLocaleFR from 'antd/locale/fr_FR';
import antLocaleJP from 'antd/locale/ja_JP';
import antLocaleKO from 'antd/locale/ko_KR';
import antLocalePT from 'antd/locale/pt_PT';
import antLocaleZH from 'antd/locale/zh_CN';
import antLocaleES from 'antd/locale/es_ES';
import antLocaleRU from 'antd/locale/ru_RU';
import antLocaleAR from 'antd/locale/ar_EG';
import antLocaleIT from 'antd/locale/it_IT';
import antLocaleTR from 'antd/locale/tr_TR';
import antLocaleHI from 'antd/locale/hi_IN';
import antLocaleSV from 'antd/locale/sv_SE';
import antLocaleNB from 'antd/locale/nb_NO';
import antLocaleFI from 'antd/locale/fi_FI';
import antLocalePL from 'antd/locale/pl_PL';
import antLocaleDA from 'antd/locale/da_DK';
import antLocaleVI from 'antd/locale/vi_VN';
import antLocaleEN from 'antd/locale/en_US';
import { ConfigProvider } from 'antd';

import { brand } from 'src/styles/colors';

import { useAuth } from './use-auth';
import { normalizeLanguage } from './i18n';
import { safeSetItem } from './safe-storage';

const getLocale = (code: string) => {
  switch (code) {
    case 'de':
      return antLocaleDE;
    case 'nl':
      return antLocaleNL;
    case 'fr':
      return antLocaleFR;
    case 'ja':
      return antLocaleJP;
    case 'ko':
      return antLocaleKO;
    case 'pt':
      return antLocalePT;
    case 'zh':
      return antLocaleZH;
    case 'es':
      return antLocaleES;
    case 'ru':
      return antLocaleRU;
    case 'ar':
      return antLocaleAR;
    case 'it':
      return antLocaleIT;
    case 'tr':
      return antLocaleTR;
    case 'hi':
      return antLocaleHI;
    case 'sv':
      return antLocaleSV;
    case 'nb':
      return antLocaleNB;
    case 'fi':
      return antLocaleFI;
    case 'pl':
      return antLocalePL;
    case 'da':
      return antLocaleDA;
    case 'vi':
      return antLocaleVI;
    default:
      return antLocaleEN;
  }
};

export function persistLanguage(language: string) {
  safeSetItem('localStorage', 'i18nextLng', language);
}

/**
 * Provider that is responsible for:
 * 1. Setting Moment.js locale based on `user.language` (authenticated) or persisted preference (unauthenticated).
 * 2. Syncing the shared i18n instance with the preferred language.
 * 3. Cleaning sensitive tokens from the URL once the router is ready.
 */
export function ProvideLocale({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { i18n } = useTranslation();
  const { user } = useAuth();

  const targetLanguage = normalizeLanguage(user?.language);
  const activeLanguage = user
    ? targetLanguage
    : normalizeLanguage(i18n.language);

  useEffect(() => {
    moment.locale(activeLanguage);
  }, [activeLanguage]);

  useEffect(() => {
    if (!router.isReady || !i18n.isInitialized) return;

    const desiredLanguage = user
      ? targetLanguage
      : normalizeLanguage(i18n.language);

    if (typeof window !== 'undefined') {
      persistLanguage(desiredLanguage);
    }

    if (i18n.language !== desiredLanguage) {
      i18n.changeLanguage(desiredLanguage).catch((error) => {
        console.error('Failed to change language', error);
      });
    }
  }, [router.isReady, targetLanguage, i18n.isInitialized, i18n.language, user]);

  useEffect(() => {
    if (!router.isReady) return;

    const { pathname, query, asPath } = router;
    if (
      query.token ||
      query.api_key ||
      asPath.includes('token') ||
      asPath.includes('api_key')
    ) {
      const nextQuery = { ...query };
      delete nextQuery.token;
      delete nextQuery.api_key;
      router.replace({ pathname, query: nextQuery }, undefined, {
        shallow: true,
      });
    }
  }, [router]);

  return (
    <ConfigProvider
      locale={getLocale(activeLanguage)}
      theme={{
        token: {
          colorPrimary: brand.primary,
          colorText: brand.text,
          colorTextSecondary: brand.textSecondary,
          colorTextHeading: brand.heading,
          colorSuccess: brand.green,
          colorError: brand.red,
          colorWarning: brand.orange,
          colorInfo: brand.blue,
          borderRadius: 6,
          borderRadiusSM: 4,
          borderRadiusLG: 10,
          fontFamily: "'NoirPro', sans-serif",
        },
        components: {
          Menu: {
            darkItemBg: brand.navDarkBg,
            darkItemColor: brand.navDarkText,
            darkItemSelectedBg: '#003f7a',
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
}
